import { Injectable, inject } from '@angular/core';
import { FormlyFieldConfig, FormlyConfig } from '@ngx-formly/core';
import { OptionProps, localizePlural } from '@parashift/shared/utils';

@Injectable({
  providedIn: 'root'
})
export class FormlyValidationTranslationService {
  private formlyConfig = inject(FormlyConfig);

  init(): void {
    this.formlyConfig.addValidatorMessage('required', (err, field) => this.requiredValidationMessage(err, field));
    this.formlyConfig.addValidatorMessage('minLength', (err, field) => this.minLengthValidationMessage(err, field));
    this.formlyConfig.addValidatorMessage('maxLength', (err, field) => this.maxLengthValidationMessage(err, field));
    this.formlyConfig.addValidatorMessage('min', (err, field) => this.minValidationMessage(err, field));
    this.formlyConfig.addValidatorMessage('max', (err, field) => this.maxValidationMessage(err, field));
    this.formlyConfig.addValidatorMessage('email', (err, field) => this.emailValidationMessage(err, field));
    this.formlyConfig.addValidatorMessage('pattern', () => this.patternValidationMessage());
    this.formlyConfig.addValidatorMessage('value', () => this.valueValidationMessage());
    this.formlyConfig.addValidatorMessage('date', () => this.dateValidationMessage());
    this.formlyConfig.addValidatorMessage('datetime', () => this.datetimeValidationMessage());
    this.formlyConfig.addValidatorMessage('mime-type-format', (err) => this.mimeTypeFormatValidationMessage(err));
    this.formlyConfig.addValidatorMessage('json', () => this.jsonValidationMessage());
    this.formlyConfig.addValidatorMessage('parameter-name', () => this.parameterNameValidationMessage());
    this.formlyConfig.addValidatorMessage('allowed-characters', () => this.allowedCharactersValidationMessage());
  }

  private requiredValidationMessage(err: any, field: FormlyFieldConfig) {
    const fieldLabel = field.props.label || $localize `:@@common.empty_field:This field`;
    return $localize `:@@parashift_shared.forms.validators.required:${fieldLabel}:field: is required`;
  }

  private minLengthValidationMessage(err: any, field: FormlyFieldConfig) {
    return $localize `:@@parashift_shared.forms.validators.min_length:Should have at least ${field.props.minLength}:minLength: ${
      localizePlural(field.props.minLength as OptionProps, {
        0: $localize `:@@common.validators.characters:${field.props.minLength}:characters: characters`,
        1: $localize `:@@common.validators.character:${field.props.minLength}:character: character`,
        other: $localize `:@@common.validators.characters:${field.props.minLength}:characters: characters`,
      })
    }:pluralCharacters:.`;
  }

  private maxLengthValidationMessage(err: any, field: FormlyFieldConfig) {
    return $localize `:@@parashift_shared.forms.validators.max_length:Should have at most ${
      localizePlural(field.props.maxLength as OptionProps, {
        0: $localize `:@@common.validators.characters:${field.props.maxLength}:characters: characters`,
        1: $localize `:@@common.validators.character:${field.props.maxLength}:character: character`,
        other: $localize `:@@common.validators.characters:${field.props.maxLength}:characters: characters`,
      })
    }:pluralCharacters:.`;
  }

  private minValidationMessage(err: any, field: FormlyFieldConfig) {
    return $localize `:@@parashift_shared.forms.validators.min:This value should be more than or equal ${field.props.min}:number:`;
  }

  private maxValidationMessage(err: any, field: FormlyFieldConfig) {
    return $localize `:@@parashift_shared.forms.validators.max:This value should be less than or equal ${field.props.max}:number:`;
  }

  private mimeTypeFormatValidationMessage(err: any) {
    return $localize `:@@parashift_shared.forms.validators.mime_type_format:Please use pattern 'type/subtype[+suffix]' at line: ${err}:lineNumbers:`;
  }

  private emailValidationMessage(err: any, field: FormlyFieldConfig) {
    return $localize `:@@parashift_shared.forms.validators.email:${field.formControl.value}:email: is not a valid email address`;
  }

  private patternValidationMessage() {
    return $localize `:@@parashift_shared.forms.validators.pattern:Invalid format`;
  }

  private valueValidationMessage() {
    return $localize `:@@parashift_shared.forms.validators.value:Invalid value`;
  }

  private dateValidationMessage() {
    return $localize `:@@parashift_shared.forms.validators.date:Invalid date`;
  }

  private datetimeValidationMessage() {
    return $localize `:@@parashift_shared.forms.validators.datetime:Invalid date or time`;
  }

  private jsonValidationMessage() {
    return $localize `:@@parashift_shared.forms.validators.json:Invalid JSON`;
  }

  private parameterNameValidationMessage() {
    return $localize `:@@parashift_shared.forms.validators.parameter_name:Invalid parameter name`;
  }

  private allowedCharactersValidationMessage() {
    return $localize `:@@parashift_shared.forms.validators.allowed_characters:This field contains invalid characters`;
  }
}
